import { useEffect, useState } from "react";
import { api } from "@/api/client";
import {
  READ_ONLY_TITLE,
  useReadOnly,
  useStore,
  usePhase,
} from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { ConfirmDialog } from "./ConfirmDialog";

/**
 * Seitenleisten-Teil der Sitzungssteuerung: Vorbereitung und Notfall.
 *
 * Der Ablauf selbst — Schritte, Weiter, Timer, Lage — steht in der
 * `ArModerationsleiste` unten in der Inhaltsfläche. Grund: Der Moderator fährt
 * einen Bildschirm und klappt die Navigation zum Vortrag ein; was er in den 25
 * Minuten braucht, darf nicht in der Navigation liegen.
 *
 * Hier bleibt, was nicht zum Vortragsfluss gehört:
 *
 * - **Rahmenthema** — wird vorbereitet, nicht vorgeführt. Die Runde wählt auf
 *   der Themen-Folie; dieses Feld ist für den Moderator, der vorher etwas
 *   setzen oder danach etwas korrigieren will.
 * - **Reserve** — der Notfallgriff. Er soll ausdrücklich *nicht* auf der
 *   Leinwand stehen: das Gremium soll nicht merken, dass es vorbereitete
 *   Ergebnisse gibt.
 * - **Nächste Gruppe** — zwischen zwei Besuchergruppen, nicht während einer.
 */
export function ArSessionControl({
  collapsed = false,
  onToggleForceSlides,
}: {
  collapsed?: boolean;
  /** Für den Notfallgriff: gibt die Leinwand an die Ergebnis-Oberfläche frei. */
  onToggleForceSlides?: (next: boolean) => void;
}) {
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const phase = usePhase();
  const readOnly = useReadOnly();
  const config = useOffsiteConfig();
  const preparedTopics = config.preparedTopics ?? [];

  const [framing, setFraming] = useState("");
  const [framingSaved, setFramingSaved] = useState<string | null>(null);
  const [framingBusy, setFramingBusy] = useState(false);
  const [framingErr, setFramingErr] = useState<string | null>(null);

  // Reserve (im Admin „Notfall-Modus"): vorbereitete Ergebnisse statt
  // Live-Generierung. Der Zustand liegt im Backend, hier nur der Spiegel.
  const [reserveAn, setReserveAn] = useState<boolean | null>(null);
  const [reserveAnzahl, setReserveAnzahl] = useState(0);
  const [reserveBusy, setReserveBusy] = useState(false);
  const [reserveErr, setReserveErr] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetLabel, setResetLabel] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [resetErr, setResetErr] = useState<string | null>(null);

  // Lage-Zeile: wie viele der erzeugten Codes je Rolle schon eingelöst sind.
  // Nur bei Code-Login relevant — ohne Codes gibt es nichts zu zählen. Poll
  // alle 10s: gröber als die Codes-Verwaltung selbst, reicht aber, um während
  // des Einlasses mitzuverfolgen, ohne die Seitenleiste zu überladen.
  const [codeLage, setCodeLage] = useState<{
    buehneEingeloest: number;
    buehneGesamt: number;
    hintergrundEingeloest: number;
    hintergrundGesamt: number;
  } | null>(null);

  // Aktuelles Rahmenthema beim Öffnen laden, damit der Moderator sieht, was die
  // Teilnehmer vorausgefüllt bekommen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.framing.get(token);
        if (cancelled) return;
        setFraming(r.topic ?? "");
        if (r.topic) setFramingSaved(r.topic);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setFramingErr("Rahmenthema konnte nicht geladen werden.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Reserve-Zustand einmal laden. Kein Poll: außer diesem Pult schaltet niemand
  // daran, und ein Fehlschlag darf die Sektion nicht blockieren.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.admin.fallback(token);
        if (cancelled) return;
        setReserveAn(r.enabled);
        setReserveAnzahl(r.preparedCount);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setReserveAn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Codes-Lage pollen — nur bei Code-Login, sonst gibt es keine Codes.
  useEffect(() => {
    if (!config.codeLogin) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.admin.listCodes(token);
        if (cancelled) return;
        const buehne = r.codes.filter((c) => c.rolle === "buehne");
        const hintergrund = r.codes.filter((c) => c.rolle === "hintergrund");
        setCodeLage({
          buehneEingeloest: buehne.filter((c) => c.usedAt).length,
          buehneGesamt: buehne.length,
          hintergrundEingeloest: hintergrund.filter((c) => c.usedAt).length,
          hintergrundGesamt: hintergrund.length,
        });
      } catch (e) {
        if (cancelled) return;
        console.error(e);
      }
    };
    poll();
    const id = window.setInterval(poll, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token, config.codeLogin]);

  const saveFraming = async () => {
    const topic = framing.trim();
    if (!topic) {
      setFramingErr("Bitte ein Rahmenthema eingeben.");
      return;
    }
    setFramingBusy(true);
    setFramingErr(null);
    try {
      const r = await api.framing.set(token, topic);
      setFramingSaved(r.topic ?? topic);
    } catch (e) {
      console.error(e);
      setFramingErr("Rahmenthema konnte nicht gespeichert werden.");
    } finally {
      setFramingBusy(false);
    }
  };

  /**
   * Der Notfallgriff: Ergebnisse auf die Leinwand, ohne auf die Generierung zu
   * warten. Ein Klick, keine Rückfrage — wer ihn drückt, steht vor Publikum und
   * hat kein zweites Modal übrig. Rückgängig geht über „Reserve: an/aus".
   *
   * Sind vorbereitete Ergebnisse hinterlegt, schaltet er zusätzlich darauf um.
   * Sind keine hinterlegt, wechselt er nur auf die Ergebnis-Ansicht — und die
   * Beschriftung sagt das vorher, damit niemand Rettung erwartet, wo keine ist.
   */
  const ergebnisseJetztZeigen = async () => {
    setReserveBusy(true);
    setReserveErr(null);
    try {
      if (reserveAnzahl > 0 && reserveAn !== true) {
        const r = await api.admin.setFallback(token, true);
        setReserveAn(r.enabled);
        setReserveAnzahl(r.preparedCount);
      }
      const p = await api.phase.set(token, "reveal");
      dispatch({ type: "phase/set", phase: p });
      // Folien-Sperre lösen, sonst bliebe die stehende Folie auf der Leinwand,
      // während die Ergebnisse fertig warten.
      onToggleForceSlides?.(false);
    } catch (e) {
      console.error(e);
      setReserveErr("Fehlgeschlagen — die Leinwand steht unverändert.");
    } finally {
      setReserveBusy(false);
    }
  };

  const reserveUmschalten = async () => {
    const ziel = reserveAn !== true;
    setReserveBusy(true);
    setReserveErr(null);
    try {
      const r = await api.admin.setFallback(token, ziel);
      setReserveAn(r.enabled);
      setReserveAnzahl(r.preparedCount);
    } catch (e) {
      console.error(e);
      setReserveErr(
        ziel
          ? "Umschalten fehlgeschlagen — die Reserve ist NICHT aktiv."
          : "Abschalten fehlgeschlagen — die Reserve läuft weiter.",
      );
    } finally {
      setReserveBusy(false);
    }
  };

  const openReset = () => {
    setResetLabel("");
    setResetMsg(null);
    setResetErr(null);
    setConfirmOpen(true);
  };

  const doReset = async () => {
    setResetBusy(true);
    setResetErr(null);
    setResetMsg(null);
    try {
      const r = await api.admin.resetGroup(token, resetLabel.trim() || undefined);
      // Scope "gruppe" lässt die Phase bewusst stehen (nicht das Thema — das
      // wählt jede Gruppe neu) — für die nächste Gruppe muss sie explizit
      // zurück auf den Warte-Screen.
      const p = await api.phase.set(token, "waiting");
      dispatch({ type: "phase/set", phase: p });
      setResetMsg(
        `${r.itemCount} Einträge archiviert — auch das Rahmenthema. Phase steht auf „Warten auf Gäste“.`,
      );
      setConfirmOpen(false);
    } catch (e) {
      console.error(e);
      setResetErr("Gruppenwechsel fehlgeschlagen — nichts wurde zurückgesetzt.");
      setConfirmOpen(false);
    } finally {
      setResetBusy(false);
    }
  };

  const sessionLive = phase === "working" || phase === "overnight";

  return (
    <div className="ar-session">
      {config.codeLogin && codeLage && !collapsed && (
        <div className="ar-block" data-testid="ar-code-lage">
          <div className="ar-block-title">Lage</div>
          <p className="ar-note">
            Bühne {codeLage.buehneEingeloest}/{codeLage.buehneGesamt} eingelöst
            {" · "}
            Hintergrund {codeLage.hintergrundEingeloest}/
            {codeLage.hintergrundGesamt} eingelöst
          </p>
        </div>
      )}

      {!collapsed && (
        <div className="ar-block">
          <div className="ar-block-title">Rahmenthema</div>
          <div className="ar-chip-row">
            {preparedTopics.map((t) => (
              <button
                key={t.short}
                type="button"
                className={`ar-chip${framing === t.full ? " is-picked" : ""}`}
                title={t.full}
                onClick={() => {
                  setFraming(t.full);
                  setFramingErr(null);
                }}
              >
                {t.short}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="input-text ar-input"
            aria-label="Rahmenthema"
            placeholder="oder frei eingeben…"
            value={framing}
            onChange={(e) => {
              setFraming(e.target.value);
              setFramingErr(null);
            }}
            disabled={framingBusy || readOnly}
          />
          <button
            type="button"
            className="btn btn-primary ar-wide-btn"
            onClick={saveFraming}
            disabled={framingBusy || readOnly}
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            {framingBusy ? "Speichere…" : "Thema speichern"}
          </button>
          {framingSaved && (
            <p className="ar-note" role="status">
              Gesetzt: {framingSaved}
            </p>
          )}
          {framingSaved && (
            <p className="ar-note" data-testid="reserve-hinweis">
              Die Reserve zu diesem Thema entsteht, sobald Sie das Prompten
              öffnen — sechs Beispiele, fertig in gut zwei Minuten.
            </p>
          )}
          {framingErr && (
            <p className="ar-note is-error" role="alert">
              {framingErr}
            </p>
          )}
        </div>
      )}

      {/* Reserve — der Notfallgriff. Bewusst nüchtern beschriftet und bewusst
       *  hier und nicht in der Moderationsleiste: die Leiste steht auf demselben
       *  Bild wie die Folie, und „Notfall" verrät dem Gremium genau das, was es
       *  nicht merken soll. Was die Knöpfe tun, steht im Tooltip. */}
      <div className="ar-block ar-reserve">
        {!collapsed && <div className="ar-block-title">Reserve</div>}
        <button
          type="button"
          className="nav-item ar-step"
          onClick={ergebnisseJetztZeigen}
          disabled={reserveBusy || reserveAn === null || readOnly}
          data-testid="ar-ergebnisse-jetzt"
          title={
            readOnly
              ? READ_ONLY_TITLE
              : reserveAnzahl > 0
                ? "Schaltet auf die vorbereiteten Ergebnisse und bringt sie sofort auf die Leinwand"
                : "Bringt die Ergebnis-Ansicht auf die Leinwand — es sind keine vorbereiteten Ergebnisse hinterlegt"
          }
        >
          <span className="nav-icon" aria-hidden="true">
            ▸
          </span>
          <span className="nav-text">
            <span className="nav-label">
              {reserveBusy ? "…" : "Ergebnisse jetzt zeigen"}
            </span>
            <span className="nav-sub">
              {reserveAnzahl > 0
                ? "ein Griff — ohne auf die Generierung zu warten"
                : "nichts hinterlegt — es käme das Echte"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`nav-item ar-step${reserveAn === true ? " active" : ""}`}
          onClick={reserveUmschalten}
          disabled={reserveBusy || reserveAn === null || readOnly}
          data-testid="ar-reserve-toggle"
          title={
            readOnly
              ? READ_ONLY_TITLE
              : reserveAn === true
                ? "Zurück auf die live erzeugten Ergebnisse"
                : "Vorbereitete Ergebnisse aus einem früheren Durchlauf statt Live-Generierung"
          }
        >
          {/* Andere Zeichen als die Ziffern der Ablaufleiste — im eingeklappten
           *  Zustand muss der Notfallgriff auf einen Blick erkennbar sein. */}
          <span className="nav-icon" aria-hidden="true">
            {reserveAn === true ? "◆" : "◇"}
          </span>
          <span className="nav-text">
            <span className="nav-label">
              {reserveAn === true ? "Reserve: an" : "Reserve: aus"}
            </span>
            <span className="nav-sub">
              {reserveAnzahl === 0
                ? "nichts hinterlegt"
                : `${reserveAnzahl} hinterlegt`}
            </span>
          </span>
        </button>
        {reserveAn === true && !collapsed && (
          <p className="ar-note is-warn" data-testid="ar-reserve-hinweis">
            Reserve aktiv — vorbereitete Ergebnisse.
          </p>
        )}
        {reserveErr && (
          <p className="ar-note is-error" role="alert">
            {reserveErr}
          </p>
        )}
      </div>

      <div className="ar-block">
        <button
          type="button"
          className="nav-item nav-item-reset"
          onClick={openReset}
          disabled={resetBusy || readOnly}
          title={readOnly ? READ_ONLY_TITLE : "Für die nächste Besuchergruppe zurücksetzen"}
        >
          <span className="nav-icon" aria-hidden="true">
            ↻
          </span>
          <span className="nav-text">
            <span className="nav-label">
              {resetBusy ? "Archiviere…" : "Nächste Gruppe"}
            </span>
            <span className="nav-sub">Prompts &amp; Ergebnisse ins Archiv</span>
          </span>
        </button>
        {!collapsed && resetMsg && (
          <p className="ar-note" role="status">
            {resetMsg}
          </p>
        )}
        {resetErr && (
          <p className="ar-note is-error" role="alert">
            {resetErr}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Für die nächste Gruppe zurücksetzen?"
        message={
          (sessionLive ? "Achtung: Die Sitzung läuft gerade.\n\n" : "") +
          "Die Prompts, die generierten Ergebnisse und das gewählte Thema dieser " +
          "Besuchergruppe wandern ins Archiv. Die nächste Gruppe wählt ihr Thema " +
          "selbst; die Sitzung startet wieder bei „Warten auf Gäste“."
        }
        confirmLabel="Ja, nächste Gruppe"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doReset}
        onCancel={() => setConfirmOpen(false)}
        busy={resetBusy}
        input={{
          label: "Label für das Archiv (optional)",
          value: resetLabel,
          onChange: setResetLabel,
          placeholder: "z.B. Gruppe 2 · 14:30",
          hint: "Hilft später, den Durchlauf im Archiv wiederzufinden.",
        }}
      />
    </div>
  );
}
